import React, { useState, useEffect, useContext } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown, Search, Image, Waves, BarChart2 } from "lucide-react";
import { HeadlineGroup, Headline, Insight, HeadlineGroupStat } from '@/types';
import { useStore } from '@/store';
import { SummaryPopover } from '@/components/shared/SummaryPopover';
import { Button } from "@/components/ui/button";
import { cn } from '@/utils/utils';
import { DataContext } from '@/db/ShapeConnector';

// Interface for search results
interface SearchResults {
  headlines: HeadlineGroup[];
  streams: any[]; // Replace with proper type when available
  images: any[]; // Replace with proper type when available
  stats: HeadlineGroupStat[]; // Add stats to search results
}

// Section component for collapsible sections
interface SearchSectionProps {
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  isEmpty: boolean;
  children: React.ReactNode;
}

function SearchSection({ title, icon, isExpanded, onToggle, isEmpty, children }: SearchSectionProps) {
  return (
    <div className="mb-4 border rounded-md overflow-hidden">
      <div 
        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-medium">{title}</h3>
          {isEmpty && <span className="text-sm text-gray-500 ml-2">(No results)</span>}
        </div>
        <ChevronDown className={cn(
          "h-5 w-5 transition-transform duration-200",
          isExpanded ? "transform rotate-180" : ""
        )} />
      </div>
      {isExpanded && (
        <div className="p-2">
          {children}
        </div>
      )}
    </div>
  );
}

// Headline results component
interface HeadlineResultsProps {
  headlines: HeadlineGroup[];
  searchQuery: string;
}

function HeadlineResults({ headlines, searchQuery }: HeadlineResultsProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (id: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  if (headlines.length === 0) {
    return <div className="text-gray-500 p-2">No headline results found for "{searchQuery}"</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[50px]"></TableHead>
          <TableHead>Headline</TableHead>
          <TableHead className="w-[150px]">Date</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {headlines.map((group) => (
          <React.Fragment key={group.headline_group_id}>
            <TableRow 
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => toggleRow(group.headline_group_id)}
            >
              <TableCell>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  expandedRows.has(group.headline_group_id) ? "transform rotate-180" : ""
                )} />
              </TableCell>
              <TableCell>
                <div className="font-medium">{group.headline_group_name}</div>
              </TableCell>
              <TableCell>
                {new Date(group.created_at).toLocaleDateString()}
              </TableCell>
            </TableRow>
            {expandedRows.has(group.headline_group_id) && (
              <TableRow>
                <TableCell colSpan={3} className="p-0">
                  <div className="p-4 bg-gray-50">
                    {group.summary && (
                      <div className="mb-3">
                        <h4 className="text-sm font-medium mb-1">Summary</h4>
                        <p className="text-sm text-gray-700">{group.summary}</p>
                      </div>
                    )}
                    {group.keywords && group.keywords.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-1">Keywords</h4>
                        <div className="flex flex-wrap gap-1">
                          {group.keywords.map((keyword, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
}

// Stream results component
interface StreamResultsProps {
  streams: any[]; // Replace with proper type when available
  searchQuery: string;
}

function StreamResults({ streams, searchQuery }: StreamResultsProps) {
  if (streams.length === 0) {
    return <div className="text-gray-500 p-2">No stream results found for "{searchQuery}"</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Stream</TableHead>
          <TableHead className="w-[150px]">Topic</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {streams.map((stream) => (
          <TableRow key={stream.stream_id} className="cursor-pointer hover:bg-gray-50">
            <TableCell>
              <div className="font-medium">{stream.stream_topic}</div>
              <div className="text-sm text-gray-500">{stream.stream_description}</div>
            </TableCell>
            <TableCell>{stream.stream_topic}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Image results component
interface ImageResultsProps {
  images: any[]; // Replace with proper type when available
  searchQuery: string;
}

function ImageResults({ images, searchQuery }: ImageResultsProps) {
  if (images.length === 0) {
    return <div className="text-gray-500 p-2">No image results found for "{searchQuery}"</div>;
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      {images.map((image, index) => (
        <div key={index} className="border rounded-md overflow-hidden">
          <div className="h-32 bg-gray-200 flex items-center justify-center">
            <Image className="h-8 w-8 text-gray-400" />
          </div>
          <div className="p-2">
            <div className="text-sm font-medium truncate">{image.title}</div>
            <div className="text-xs text-gray-500">{image.source}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Stats results component
interface StatsResultsProps {
  stats: HeadlineGroupStat[];
  searchQuery: string;
}

function StatsResults({ stats, searchQuery }: StatsResultsProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (id: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  if (stats.length === 0) {
    return <div className="text-gray-500 p-2">No vector search results found for "{searchQuery}"</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[50px]"></TableHead>
          <TableHead>Headline Group</TableHead>
          <TableHead className="w-[100px]">Sentiment</TableHead>
          <TableHead className="w-[100px]">Impact</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {stats.map((stat, index) => (
          <React.Fragment key={stat.headline_group_id}>
            <TableRow 
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => toggleRow(stat.headline_group_id)}
            >
              <TableCell>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  expandedRows.has(stat.headline_group_id) ? "transform rotate-180" : ""
                )} />
              </TableCell>
              <TableCell>
                <div className="font-medium">Group #{stat.headline_group_id}</div>
                <div className="text-xs text-gray-500">Rank #{index + 1} in vector similarity</div>
              </TableCell>
              <TableCell>
                <div className={cn(
                  "px-2 py-1 rounded-full text-xs font-medium text-center",
                  stat.sentiment_score > 75 ? "bg-green-100 text-green-800" :
                  stat.sentiment_score > 50 ? "bg-blue-100 text-blue-800" :
                  stat.sentiment_score > 25 ? "bg-yellow-100 text-yellow-800" :
                  "bg-red-100 text-red-800"
                )}>
                  {stat.sentiment_score}%
                </div>
              </TableCell>
              <TableCell>
                <div className={cn(
                  "px-2 py-1 rounded-full text-xs font-medium text-center",
                  stat.impact_score > 75 ? "bg-purple-100 text-purple-800" :
                  stat.impact_score > 50 ? "bg-indigo-100 text-indigo-800" :
                  stat.impact_score > 25 ? "bg-blue-100 text-blue-800" :
                  "bg-gray-100 text-gray-800"
                )}>
                  {stat.impact_score}%
                </div>
              </TableCell>
            </TableRow>
            {expandedRows.has(stat.headline_group_id) && (
              <TableRow>
                <TableCell colSpan={4} className="p-0">
                  <div className="p-4 bg-gray-50">
                    {stat.text_main && (
                      <div className="mb-3">
                        <h4 className="text-sm font-medium mb-1">Main Text</h4>
                        <p className="text-sm text-gray-700">{stat.text_main}</p>
                      </div>
                    )}
                    {stat.text_context && (
                      <div>
                        <h4 className="text-sm font-medium mb-1">Context</h4>
                        <p className="text-sm text-gray-700">{stat.text_context}</p>
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <h4 className="text-xs font-medium text-gray-500">Vector Similarity Search Result</h4>
                      <p className="text-xs text-gray-500 mt-1">This result was found using vector similarity search with a random test vector.</p>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
}

interface SearchPanelProps {
  searchQuery: string;
}

export default function SearchPanel({ searchQuery }: SearchPanelProps) {
  const dataContext = useContext(DataContext);
  
  const [expandedSections, setExpandedSections] = useState<{
    headlines: boolean;
    streams: boolean;
    images: boolean;
    stats: boolean;
  }>({
    headlines: true,
    streams: true,
    images: true,
    stats: true
  });

  const [searchResults, setSearchResults] = useState<SearchResults>({
    headlines: [],
    streams: [],
    images: [],
    stats: []
  });

  const [isLoading, setIsLoading] = useState(true);

  const { headlineGroups, streams } = useStore();

  // Handle search results based on the query
  useEffect(() => {
    if (!searchQuery) {
      setSearchResults({
        headlines: [],
        streams: [],
        images: [],
        stats: []
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const fetchResults = async () => {
      try {
        // Filter headline groups that match the search query
        const filteredHeadlines = headlineGroups.filter(group => 
          group.headline_group_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (group.summary && group.summary.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (group.keywords && group.keywords.some(keyword => 
            keyword.toLowerCase().includes(searchQuery.toLowerCase())
          ))
        );

        // Filter streams that match the search query
        const filteredStreams = streams.filter(stream => 
          stream.stream_topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
          stream.stream_description.toLowerCase().includes(searchQuery.toLowerCase())
        );

        // Perform vector similarity search if context is available
        let vectorResults: HeadlineGroupStat[] = [];
        if (dataContext && searchQuery.trim() !== '') {
          try {
            console.log('Performing vector search for query:', searchQuery);
            vectorResults = await dataContext.performVectorSearch(searchQuery);
            console.log('Vector search returned results:', vectorResults.length);
          } catch (error) {
            console.error('Error performing vector search:', error);
          }
        } else {
          console.log('DataContext not available or empty query, skipping vector search');
        }

        // For now, we'll just use empty array for images
        setSearchResults({
          headlines: filteredHeadlines,
          streams: filteredStreams,
          images: [],
          stats: vectorResults
        });
      } catch (error) {
        console.error('Error fetching search results:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Use a debounce to avoid too many searches while typing
    const timer = setTimeout(() => {
      fetchResults();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, headlineGroups, streams, dataContext]);

  const toggleSection = (section: 'headlines' | 'streams' | 'images' | 'stats') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!searchQuery) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <Search className="h-12 w-12 mb-4 text-gray-300" />
        <p className="text-lg">Enter a search query to see results</p>
      </div>
    );
  }

  const hasResults = 
    searchResults.headlines.length > 0 || 
    searchResults.streams.length > 0 || 
    searchResults.images.length > 0 ||
    searchResults.stats.length > 0;

  if (!hasResults) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <Search className="h-12 w-12 mb-4 text-gray-300" />
        <p className="text-lg">No results found for "{searchQuery}"</p>
        <p className="text-sm mt-2">Try a different search term or check your spelling</p>
      </div>
    );
  }

  return (
    <div className="p-4 h-full overflow-auto">
      <div className="mb-4">
        <h2 className="text-xl font-semibold">Search results for "{searchQuery}"</h2>
        <p className="text-sm text-gray-500">
          Found {searchResults.headlines.length} headline results, 
          {searchResults.streams.length} stream results, 
          {searchResults.images.length} image results, and
          {searchResults.stats.length} vector search results
        </p>
      </div>

      <SearchSection 
        title="Headline Group Results" 
        icon={<BarChart2 className="h-5 w-5" />}
        isExpanded={expandedSections.stats}
        onToggle={() => toggleSection('stats')}
        isEmpty={searchResults.stats.length === 0}
      >
        <StatsResults stats={searchResults.stats} searchQuery={searchQuery} />
      </SearchSection>

      <SearchSection 
        title="Headline Results" 
        icon={<Search className="h-5 w-5" />}
        isExpanded={expandedSections.headlines}
        onToggle={() => toggleSection('headlines')}
        isEmpty={searchResults.headlines.length === 0}
      >
        <HeadlineResults headlines={searchResults.headlines} searchQuery={searchQuery} />
      </SearchSection>

      <SearchSection 
        title="Stream Results" 
        icon={<Waves className="h-5 w-5" />}
        isExpanded={expandedSections.streams}
        onToggle={() => toggleSection('streams')}
        isEmpty={searchResults.streams.length === 0}
      >
        <StreamResults streams={searchResults.streams} searchQuery={searchQuery} />
      </SearchSection>

      <SearchSection 
        title="Image Results" 
        icon={<Image className="h-5 w-5" />}
        isExpanded={expandedSections.images}
        onToggle={() => toggleSection('images')}
        isEmpty={searchResults.images.length === 0}
      >
        <ImageResults images={searchResults.images} searchQuery={searchQuery} />
      </SearchSection>
    </div>
  );
} 